import axios from 'axios';
import Config from 'context/config';

export default class Api {
  constructor(state, setAppState, getAppState) {
    this.setAppState = setAppState;
    this.getAppState = getAppState;

    // Currently supported API version
    this.api_version = "v1";

    // Default client for basic operations
    this.axios = axios.create({
      baseURL: Config.apiBaseUrl()
    });

    // Default client for basic authenticated operations
    this.isAuthenticating = false;
    this.authSubscribers = [];
    this.axiosAuth = axios.create({
      baseURL: Config.apiBaseUrl()
    });
    this.axiosAuth.interceptors.request.use((config) => { return this.auth_header_interceptor(config) }, null);

    // Client capable of refreshing token in response to auth errors
    this.isRefreshing = false;
    this.refreshSubscribers = [];
    this.axiosRefresh = axios.create({
      baseURL: Config.apiBaseUrl()
    });
    this.axiosRefresh.interceptors.request.use((config) => { return this.auth_header_interceptor(config) }, null);
    this.axiosRefresh.interceptors.response.use(null, (error) => { return this.authErrorInterceptor(error) });

    // Users cache
    this.usersCacheById = {};
    this._currentUserIdPromise = null;

    // Load locally cached authentication state
    this.loadLocalAuthState(state);
  }

  log(...args) {
    if ( Config.debug ) {
      console.log(...args);
    }
  }

  /**
   * Public state controls
   */

  loadLocalAuthState(state) {
    try {
      // Attempt to load creds from local storage
      const localAuth = localStorage.getItem("auth");
      const auth = JSON.parse(localAuth);
      if ( auth ) {
        state.authenticated = true;
        state.auth = auth;
      }
    } catch (err) {
      // Authentication is delayed until API interaction is requested
    }
  }

  signout() {
    // Clear authentication data
    this.unAuthenticate();
  }

  /**
   * State management
   */

  authenticate(anonymous, refreshToken, accessToken, expiresAt) {
    const promise = new Promise((resolve, reject) => {
      // Prepare auth state
      const auth = {
        anonymous: anonymous,
        refreshToken: refreshToken,
        accessToken: accessToken,
        expiresAt: expiresAt
      };

      // Persist globally
      localStorage.setItem("auth", JSON.stringify(auth));

      // Update locally
      this.setAppState({
        authenticated: true,
        auth: auth
      }, () => {
        resolve();
      });
    });
    return promise;
  }

  refreshToken(accessToken, expiresAt) {
    const promise = new Promise((resolve, reject) => {
      // Prepare auth state
      const auth = {
        anonymous: this.getAppState().auth.anonymous,
        refreshToken: this.getAppState().auth.refreshToken,
        accessToken: accessToken,
        expiresAt: expiresAt
      };

      // Persist globally
      localStorage.setItem("auth", JSON.stringify(auth));

      // Update locally
      this.setAppState({
        authenticated: true,
        auth: auth
      }, () => {
        resolve();
      });
    });
    return promise;
  }

  unAuthenticate() {
    const promise = new Promise((resolve, reject) => {
      // Clear global state
      localStorage.setItem("auth", JSON.stringify(null));

      // Clear local state
      this.setAppState({
        authenticated: false,
        auth: null
      }, () => {
        resolve();
      });
    });
    return promise;
  }

  /**
   * auth
   */

  appendAuthorizationToken(config) {
    const state = this.getAppState();
    config.headers["Authorization"] = `Bearer ${state.auth.accessToken}`;
  }

  auth_header_interceptor(config) {
    var state = this.getAppState();
    if ( state.authenticated ) {
      this.appendAuthorizationToken(config);
      return config;
    } else {
      this.log("[auth_header_interceptor] Initiating anonymous authentication...");

      this.authCognitoAnonymousGet();

      const authSubscribers = new Promise(resolve => {
        this.authSubscribers.push(() => {
          this.log("[auth_header_interceptor] Resolving...");
          this.appendAuthorizationToken(config);
          resolve(config);
        });
      });
      return authSubscribers;
    }
  }

  authErrorInterceptor(error) {
    this.log("[authErrorInterceptor] Entry point: " + error);

    if ( !error.response ) {
      return Promise.reject(error);
    }

    // Extract message and make it available as top level property for convenience
    this.appendErrorMessage(error);

    const { config, response: { status } } = error;
    const originalRequest = config;

    if ( 401 === status ) {
      this.log("[authErrorInterceptor] Initiating refresh...");

      if ( !this.isRefreshing ) {
        this.log("[authErrorInterceptor] Initial refresh request observed");

        this.isRefreshing = true;
        this.authCognitoRefreshPost().then(response => {
          this.log("[authErrorInterceptor] Notifying refresh subscribers...");

          this.isRefreshing = false;
          this.refreshSubscribers.map(callback => callback());
          this.refreshSubscribers = [];
        }, error => {
          this.log("[authErrorInterceptor] Cleaning up subscribers upon failure to refresh...");

          this.isRefreshing = false;
          this.refreshSubscribers = [];
        });
      }
      const requestSubscribers = new Promise(resolve => {
        this.refreshSubscribers.push(() => {
          resolve(this.axiosAuth(originalRequest));
        });
      });
      return requestSubscribers;
    }
    return Promise.reject(error);
  }

  authCognitoRefreshPost() {
    let that = this;
    this.log("[authCognitoRefreshPost] Refreshing access token...");

    return new Promise((resolve, reject) => {
      this.axios.post(`/${this.api_version}/auth/core/refresh`, {
        "refresh_token": this.getAppState().auth.refreshToken
      })
        .then( response => {
          this.log("[authCognitoRefreshPost] Access token obtained");

          // Remember new token
          const accessToken = response.data["access_token"];
          const expiresAt = response.data["expires_at"];
          this.refreshToken(accessToken, expiresAt).then(() => {
            // Complete request
            resolve(response);
          });
        }, error => {
          this.log("[authCognitoRefreshPost] Failed to refresh access token. Signing out...");

          // Sign out completely
          this.unAuthenticate().then(() => {
            // Complete request with failure
            reject(error);
          });
        })
        .catch(function (error) {
          that.log("[authCognitoRefreshPost] Failed to refresh token with generic error: " + error);
        });
    });
  }

  authCognitoAnonymousGet() {
    if ( this.isAuthenticating ) {
      this.log("[authCognitoAnonymousGet] Authentication is already in progress");
      return;
    }

    this.log("[authCognitoAnonymousGet] Initial auth request observed");

    this.isAuthenticating = true;
    this.authCognitoAnonymousGetCore().then(response => {
      this.log("[authCognitoAnonymousGet] Notifying auth subscribers...");

      this.isAuthenticating = false;
      this.authSubscribers.map(callback => callback());
      this.authSubscribers = [];
    }, error => {
      this.log("[authCognitoAnonymousGet] Cleaning up subscribers upon failure to auth...");

      this.isAuthenticating = false;
      this.authSubscribers = [];
    });
  }

  authCognitoAnonymousGetCore() {
    this.log("[authCognitoAnonymousGetCore] Fetching anonymous token...");

    return new Promise((resolve, reject) => {
      this.axios.get(`/${this.api_version}/auth/core/anonymous`)
        .then( response => {
          this.log("[authCognitoAnonymousGetCore] Anonymous token acquired");

          // Remember the token
          const refreshToken = response.data["refresh_token"];
          const accessToken = response.data["access_token"];
          const expiresAt = response.data["expires_at"];
          this.authenticate(true, refreshToken, accessToken, expiresAt).then(() => {
            // Complete request
            resolve(response);
          });
        }, error => {
          this.log("[authCognitoAnonymousGetCore] Failed to fetch anonymous token");

          // Sign out completely
          this.unAuthenticate();

          // Complete request with failure
          reject(error);
        })
        .catch(function (error) {
          this.log("[authCognitoAnonymousGetCore] Failed to fetch anonymous token with generic error: " + error);
        });
    });
  }

  authGoogleSigninPost(accessToken, externalId, name, email) {
    return new Promise((resolve, reject) => {
      this.log("[authGoogleSigninPost] Initiating token exchange...");

      const auth = {
        access_token: accessToken,
        email: email,
        name: name,
        external_id: externalId
      };
      this.axios.post(`/${this.api_version}/auth/google/signin`, auth)
        .then( response => {
          this.log("[authGoogleSigninPost] Token acquired");

          // Remember the token
          const refreshToken = response.data["refresh_token"];
          const accessToken = response.data["access_token"];
          const expiresAt = response.data["expires_at"];
          this.authenticate(false, refreshToken, accessToken, expiresAt);

          // Complete request
          resolve(response);
        }, error => {
          this.log("[authGoogleSigninPost] Failed to exchange token");
          reject(error);
        });
    });
  }

  /**
   * helpers
   */

  appendErrorMessage(error) {
    let message = "Unexpected error occurred";
    if ( error && error.response && error.response.data && error.response.data.message ) {
      message = error.response.data.message;
    }
    error.message = message;
  }

  /**
   * Users
   */

  usersIdGet(userId) {
    return new Promise((resolve, reject) => {
      const cachedUser = this.usersCacheById[userId];
      if (cachedUser) {
        resolve(cachedUser);
        return;
      }

      this.axiosRefresh.get(`/${this.api_version}/users/${userId}`)
        .then( response => {
          this.usersCacheById[userId] = response;
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  usersIdGetNoCache(userId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/users/${userId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  getCurrentUserId() {
    if (this._currentUserIdPromise) return this._currentUserIdPromise;
    this._currentUserIdPromise = this.usersIdGet("me")
      .then(response => {
        // /v1/users/me returns the user record under response.data.user
        // (same shape as usersIdGet for any other user id — confirmed via
        // existing callers, e.g. components/profile/access.js:166).
        const data = response.data;
        const user = (data && data.user) ? data.user : data;
        return user && user.id ? user.id : null;
      })
      .catch(err => {
        this._currentUserIdPromise = null;
        throw err;
      });
    return this._currentUserIdPromise;
  }

  usersIdPatch(userId, user) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.patch(`/${this.api_version}/users/${userId}`, user)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  users_id_orgs_get(userId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/users/${userId}/orgs`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  usersIdLimitsGet(userId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/users/${userId}/limits`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  isSelfUser(user) {
    return "owner" === user.role;
  }

  /**
   * Credentials
   */

  credentialsTargetTargetIdGet(target, targetId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/credentials/target/${target}/${targetId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  credentialsTargetTargetIdPost(target, targetId, credential) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/credentials/target/${target}/${targetId}`, credential)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  credentialsIdDelete(credentialId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.delete(`/${this.api_version}/credentials/${credentialId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  credentialsManifestGet() {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/credentials/manifest`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  /**
   * Organizations
   */

  orgsPost(org) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/orgs`, org)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  orgsIdGet(orgId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/orgs/${orgId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  orgsIdPatch(orgId, org) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.patch(`/${this.api_version}/orgs/${orgId}`, org)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  orgsIdBlobsGet(orgId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/orgs/${orgId}/blobs`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  blobsPost(orgId, blob) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/orgs/${orgId}/blobs`, blob)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  orgsIdLimitsGet(orgId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/orgs/${orgId}/limits`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  orgsIdDelete(orgId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.delete(`/${this.api_version}/orgs/${orgId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  canAdministerOrg(org) {
    return "admin" === org.role || "owner" === org.role;
  }

  canAccessOrgSettings(org) {
    return !!org.role;
  }

  /**
   * Blobs
   */

  blobsIdGet(orgId, blobId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/blobs/${orgId}/${blobId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  blobsIdPatch(orgId, blobId, blob) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.patch(`/${this.api_version}/blobs/${orgId}/${blobId}`, blob)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  blobsIdLimitsGet(orgId, blobId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/blobs/${orgId}/${blobId}/limits`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  blobsIdRevisionsGet(orgId, blobId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/blobs/${orgId}/${blobId}/revisions`)
        .then( response => {
          // Sort revisions by creation time in descending order
          response.data.revisions = response.data.revisions.sort((r1, r2) => r1.created_at > r2.created_at ? -1 : 1);

          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  blobsIdRevisionsPost(orgId, blobId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/blobs/${orgId}/${blobId}/revisions`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  blobsIdDelete(orgId, blobId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.delete(`/${this.api_version}/blobs/${orgId}/${blobId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  isBlobSettingsAccessible(blob) {
    return "admin" === blob.role || "owner" === blob.role;
  }

  canAccessBlobSettings(blob) {
    return !!blob.role;
  }

  hasBlobReachedTerminalSuccessStatus(blob) {
    return "ready" === blob.status;
  }

  isBlobInUpdatingStatus(blob) {
    return "updating" === blob.status;
  }

  isBlobInDeletingStatus(blob) {
    return "deleting" === blob.status;
  }

  hasBlobReachedTerminalFailureStatus(blob) {
    return "failed" === blob.status;
  }

  /**
   * Blob Metadata
   */

  blobsIdMetadataAliasGet(orgId, blobId, alias) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/blobs/${orgId}/${blobId}/metadata/${alias}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  /**
   * Members
   */

  membersTargetTargetIdPost(target, targetId, member) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/members/target/${target}/${targetId}`, member)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  membersTargetTargetIdGet(target, targetId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/members/target/${target}/${targetId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  membersIdDelete(memberId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.delete(`/${this.api_version}/members/${memberId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  /**
   * Blob API Keys
   */

  apiKeysTargetTargetIdGet(target, targetId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/api-keys/target/${target}/${targetId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  apiKeysTargetTargetIdPost(target, targetId, apiKey) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/api-keys/target/${target}/${targetId}`, apiKey)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  apiKeysIdDelete(apiKeyId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.delete(`/${this.api_version}/api-keys/${apiKeyId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  blobWriteRoles = new Set(["owner", "admin", "write"]);

  haveBlobWriteAccess(blob) {
    return this.blobWriteRoles.has(blob.role);
  }

  /**
   * Revisions
   */

  revisionsIdGet(revisionId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/revisions/${revisionId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  revisionsIdDataCommandPost(revisionId, body, data) {
    let headers = {}
    if ( data ) {
      headers = {
        "Content-Type": "application/octet-stream",
        "X-Request-Body": JSON.stringify(body)
      };
    } else {
      headers = {
        "Content-Type": "application/json"
      };
      data = body;
    }
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/revisions/${revisionId}/data/command`, data, {
        headers: headers
      })
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  revisionsIdDataQueryPost(revisionId, body) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/revisions/${revisionId}/data/query`, body, {
        headers: {
          "Accept": "application/octet-stream"
        },
        responseType: "arraybuffer"
      })
        .then( response => {
          if ( "application/octet-stream" === response.headers["content-type"] ) {
            response.binary = response.data;
            response.data = JSON.parse(response.headers["x-response-body"]);
          } else if ( "application/json" === response.headers["content-type"] ) {
            const decoder = new TextDecoder("utf-8");
            const decoded = decoder.decode(response.data);
            response.data = JSON.parse(decoded);
          }
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  /**
   * Session thread items (workflow_blobhub engine)
   */

  threadItemPost(revisionId, sessionId, alias, content, parentId, metadata) {
    const body = {
      engine: "workflow_blobhub",
      command: "post_session_thread_item",
      session_id: sessionId,
      alias,
      content
    };
    if (parentId !== undefined && parentId !== null) body.parent_id = parentId;
    if (metadata !== undefined && metadata !== null) body.metadata = metadata;
    return this.revisionsIdDataCommandPost(revisionId, body);
  }

  threadItemsList(revisionId, sessionId, alias, options) {
    const body = {
      engine: "workflow_blobhub",
      command: "list_session_thread_items",
      session_id: sessionId,
      alias
    };
    const opts = options || {};
    if (opts.ascending !== undefined) body.ascending = opts.ascending;
    if (opts.createdSince !== undefined) body.created_since = opts.createdSince;
    if (opts.createdBefore !== undefined) body.created_before = opts.createdBefore;
    if (opts.limit !== undefined) body.limit = opts.limit;
    if (opts.cursor) body.cursor = opts.cursor;
    return this.revisionsIdDataQueryPost(revisionId, body);
  }

  threadItemGet(revisionId, sessionId, alias, itemId) {
    const body = {
      engine: "workflow_blobhub",
      command: "get_session_thread_item",
      session_id: sessionId,
      alias,
      item_id: itemId
    };
    return this.revisionsIdDataQueryPost(revisionId, body);
  }

  /**
   * Session graph (workflow_blobhub engine)
   */

  applySessionGraphMutations(revisionId, sessionId, alias, operations) {
    const body = {
      engine: "workflow_blobhub",
      command: "apply_session_graph_mutations",
      session_id: sessionId,
      alias,
      operations
    };
    return this.revisionsIdDataCommandPost(revisionId, body);
  }

  querySessionGraph(revisionId, sessionId, alias, query) {
    const body = {
      engine: "workflow_blobhub",
      command: "query_session_graph",
      session_id: sessionId,
      alias,
      query
    };
    return this.revisionsIdDataQueryPost(revisionId, body);
  }

  listSessionGraphElements(revisionId, sessionId, alias, options) {
    const body = {
      engine: "workflow_blobhub",
      command: "list_session_graph_elements",
      session_id: sessionId,
      alias
    };
    const opts = options || {};
    if (opts.type !== undefined) body.type = opts.type;
    if (opts.ascending !== undefined) body.ascending = opts.ascending;
    if (opts.cursor) body.cursor = opts.cursor;
    if (opts.updated_since !== undefined) body.updated_since = opts.updated_since;
    if (opts.ids_only !== undefined) body.ids_only = opts.ids_only;
    if (opts.limit !== undefined) body.limit = opts.limit;
    return this.revisionsIdDataQueryPost(revisionId, body);
  }

  listSessionGraphNeighbors(revisionId, sessionId, alias, from, options) {
    const body = {
      engine: "workflow_blobhub",
      command: "list_session_graph_neighbors",
      session_id: sessionId,
      alias,
      from
    };
    const opts = options || {};
    if (opts.direction !== undefined) body.direction = opts.direction;
    if (opts.label !== undefined) body.label = opts.label;
    if (opts.neighbor_label !== undefined) body.neighbor_label = opts.neighbor_label;
    if (opts.cursor) body.cursor = opts.cursor;
    if (opts.limit !== undefined) body.limit = opts.limit;
    return this.revisionsIdDataQueryPost(revisionId, body);
  }

  getSessionGraphElement(revisionId, sessionId, alias, elementId) {
    const body = {
      engine: "workflow_blobhub",
      command: "get_session_graph_element",
      session_id: sessionId,
      alias,
      element_id: elementId
    };
    return this.revisionsIdDataQueryPost(revisionId, body);
  }

  getSessionGraphElements(revisionId, sessionId, alias, elementIds) {
    const body = {
      engine: "workflow_blobhub",
      command: "get_session_graph_elements",
      session_id: sessionId,
      alias,
      element_ids: elementIds
    };
    return this.revisionsIdDataQueryPost(revisionId, body);
  }

  revisionsIdCommitPost(revisionId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/revisions/${revisionId}/commit`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  revisionsIdPatch(revisionId, revision) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.patch(`/${this.api_version}/revisions/${revisionId}`, revision)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  revisionsIdDelete(revisionId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.delete(`/${this.api_version}/revisions/${revisionId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  canUpdateRevision(blob, revision) {
    return ( this.haveBlobWriteAccess(blob) && ("draft" === revision.phase || "managed" === revision.phase) );
  }

  canCommitRevision(blob, revision) {
    return ( this.haveBlobWriteAccess(blob) && "draft" === revision.phase && "ready" === revision.status );
  }

  canCreateRevision(blob, revision) {
    return (
      this.haveBlobWriteAccess(blob) &&
      revision.id === blob.latest_revision_id &&
      "commit" === revision.phase &&
      "ready" === revision.status
    );
  }

  canDeleteRevision(blob, revision) {
    return ( this.haveBlobWriteAccess(blob) && blob.latest_revision_id !== revision.id && "ready" === revision.status );
  }

  hasRevisionReachedTerminalStatus(revision) {
    return ("ready" === revision.status || "failed" === revision.status);
  }

  /**
   * Revision Metadata
   */

  revisionsIdMetadataAliasGet(revisionId, alias) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/revisions/${revisionId}/metadata/${alias}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  revisionsIdMetadataAliasPost(revisionId, alias, item) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/revisions/${revisionId}/metadata/${alias}`, item)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  revisionsMetadataIdPut(itemId, item) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.put(`/${this.api_version}/revisions/metadata/${itemId}`, item)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  revisionsMetadataIdDelete(itemId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.delete(`/${this.api_version}/revisions/metadata/${itemId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  /**
   * Operations
   */

  operationsIdGet(operationId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/operations/${operationId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  isOperationStillInProgress(operation) {
    return "in_progress" === operation.status;
  }

  hasOperationCompleted(operation) {
    return "completed" === operation.status;
  }

  hasOperationFailed(operation) {
    return "failed" === operation.status;
  }

  /**
   * Workflow
   */

  workflowManifestGet(revisionId, category) {
    const body = {
      engine: "workflow_blobhub",
      command: "get_manifest",
      category: category
    };
    return this.revisionsIdDataQueryPost(revisionId, body);
  }

  /**
   * Workflow Execution Events
   */

   hasWorkflowExecutionReachedTerminalStatus(execution) {
     return "completed" === execution.status || "failed" === execution.status || "stopped" === execution.status;
   }
}
